/**
 * puddle_runtime — the shallow-water puddle PATCH at run time (shader gaps R6, 2026-09-30): the engine's
 * `ShallowWaterGpu` driven over a camera-following `PuddleWindow` on a ground the caller supplies (`PuddleGround`: the
 * game's drawn chunks, or the bed's synthetic terrain). Shared by `modules/AtlasPuddleModule.ts` and the bed
 * (`src/probe/puddle_bed.ts`) so the picture the bed judges is the code the game runs.
 *
 *   • PLACE / FOLLOW — the window moves by WHOLE cells on one world lattice; the GPU state shifts (`ShallowWaterGpu.
 *     shift`), the CPU-owned bed / sources shift the same way, and the cells that slid in are queued PENDING (closed,
 *     look.w 0 — the ground draws its static fill there until they are built).
 *   • BUILD — a time-budgeted queue of cell rectangles: each cell's bed = the drawn ground − the static hollows' micro-
 *     relief (`puddleCell`), its ground class, its retention, a solid where a building stands, a drain where still water
 *     lies; then the edge ring (the terrain one cell beyond). A resident chunk that changes (a re-LOD, a restamp) queues
 *     its rectangle again.
 *   • STEP — the clock's rates in (`ratesFromClock`), the shelter's exposure through the mask kernel, CFL substeps.
 *   • READ — an async readback of the depth + the ledger (the budget residual) for `__puddles()` and the bed.
 *
 * NEVER AUTHORITATIVE: gameplay reads none of this.
 */
import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import { ShallowWaterGpu, type ShallowWaterGpuLedger } from 'enginex/environment/water/ShallowWaterGpu.js';
import {
  PuddleWindow, puddleCell, packCell, PUDDLE_DAMPING, PUDDLE_VISCOSITY, PUDDLE_RETAIN_FILM, RELIEF_M, reliefDepression, MASK_REFRESH_S,
  type PuddleGround, type PuddlePatchConfig, type PuddleRates,
} from './puddle_sim.js';
import { puddleChannel } from './puddle_layer.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL node graphs are loosely typed (@types/three 0.182)
type N = any;
interface Renderer { compute(nodes: unknown): unknown; getArrayBufferAsync(attr: unknown): Promise<ArrayBuffer> }

interface Rect { c0: number; c1: number; r0: number; r1: number }

export interface PuddleReadStats {
  at: number; volume: number; wetCells: number; maxDepth: number;
  ledger: ShallowWaterGpuLedger; residual: number | null; depth: Float32Array; minX: number; minZ: number;
}

export class PuddleRuntime {
  readonly gpu: ShallowWaterGpu;
  readonly win: PuddleWindow;
  readonly n: number;
  readonly cell: number;
  /** The absolute Y the bed / levels are relative to (set at placement). */
  refY = 0;
  primed = false;
  /** Cells still pending (0 = the whole window is built). */
  pending: number;
  private queue: Rect[] = [];
  /** The mask kernel's inputs: node (0, 0) in RENDER space, and refY. */
  readonly uOriginRender = uniform(new THREE.Vector2(0, 0));
  readonly uRefY = uniform(0);
  private maskClock = 0;
  readonly stats = { cellsBuilt: 0, buildMs: 0, lastBuildMs: 0, cellUs: 0, placements: 0, rebuilds: 0, readbacks: 0 };
  last: PuddleReadStats | null = null;
  private reading = false;
  private lastVolume: number | null = null;

  constructor(cfg: PuddlePatchConfig, lookAttr: THREE.StorageBufferAttribute, exposureAt: ((p: N) => N) | null) {
    this.n = cfg.n; this.cell = cfg.cell;
    this.gpu = new ShallowWaterGpu({ resolution: cfg.n, cellSize: cfg.cell, damping: PUDDLE_DAMPING, viscosity: PUDDLE_VISCOSITY, lookAttr, openEdges: true });
    this.win = new PuddleWindow(cfg.n, cfg.cell);
    for (let i = 0; i < this.n * this.n; i++) { this.gpu.src[i * 4 + 1] = 1; this.gpu.src[i * 4 + 2] = 3; }
    this.pending = this.n * this.n;
    if (exposureAt) this.gpu.setMaskFunction(exposureAt, this.uOriginRender, this.uRefY);
  }

  /** THE WARM: one kernel a call; true once every kernel's pipeline exists. */
  prime(r: Renderer): boolean { if (!this.primed) this.primed = this.gpu.prime(r); return this.primed; }

  /** Follow the camera (absolute). A first placement or a jump past half the window rebuilds everything. */
  follow(r: Renderer, x: number, z: number, groundY: (x: number, z: number) => number): 'placed' | 'shifted' | 'still' {
    const f = this.win.follow(x, z);
    const n = this.n;
    if (f === 'placed' || Math.max(Math.abs(f.dc), Math.abs(f.dr)) >= n / 2) {
      if (f !== 'placed') { const [mx, mz] = this.win.snappedMin(x, z); this.win.minX = mx; this.win.minZ = mz; }
      this.refY = groundY(this.win.centerX, this.win.centerZ);
      this.gpu.clear(r);
      for (let i = 0; i < n * n; i++) { this.gpu.src[i * 4 + 2] = 3; this.gpu.bed[i] = 0; }
      this.queue = this.stripOrder({ c0: 0, c1: n, r0: 0, r1: n }, x, z);
      this.pending = n * n;
      this.gpu.markInputsDirty();
      this.stats.placements++;
      return 'placed';
    }
    if (f.dc === 0 && f.dr === 0) return 'still';
    const { dc, dr } = f;
    this.gpu.shift(r, dc, dr);
    // the CPU-owned inputs move the same way: new[row][col] = old[row + dr][col + dc]; new strips PENDING
    const bed2 = new Float32Array(n * n), src2 = new Float32Array(n * n * 4);
    for (let row = 0; row < n; row++) for (let col = 0; col < n; col++) {
      const i = row * n + col, sc = col + dc, sr = row + dr;
      if (sc >= 0 && sc < n && sr >= 0 && sr < n) {
        const j = sr * n + sc;
        bed2[i] = this.gpu.bed[j];
        src2.set(this.gpu.src.subarray(j * 4, j * 4 + 4), i * 4);
      } else { src2[i * 4 + 1] = 1; src2[i * 4 + 2] = 3; }
    }
    this.gpu.bed.set(bed2); this.gpu.src.set(src2);
    // queued rectangles move too (still-pending cells keep their place in the world)
    this.queue = this.queue.map((q) => ({ c0: q.c0 - dc, c1: q.c1 - dc, r0: q.r0 - dr, r1: q.r1 - dr }))
      .map((q) => ({ c0: Math.max(0, q.c0), c1: Math.min(n, q.c1), r0: Math.max(0, q.r0), r1: Math.min(n, q.r1) }))
      .filter((q) => q.c1 > q.c0 && q.r1 > q.r0);
    const fresh: Rect[] = [];
    if (dc > 0) fresh.push({ c0: n - dc, c1: n, r0: 0, r1: n }); else if (dc < 0) fresh.push({ c0: 0, c1: -dc, r0: 0, r1: n });
    if (dr > 0) fresh.push({ c0: 0, c1: n, r0: n - dr, r1: n }); else if (dr < 0) fresh.push({ c0: 0, c1: n, r0: 0, r1: -dr });
    this.queue.unshift(...fresh);
    this.pending = this.countPending();
    this.gpu.markInputsDirty();
    return 'shifted';
  }

  /** Queue a world rectangle (absolute) for a rebuild — a chunk under the window changed. */
  invalidate(x0: number, z0: number, x1: number, z1: number): void {
    const n = this.n, c = this.cell;
    const r: Rect = {
      c0: Math.max(0, Math.floor((x0 - this.win.minX) / c)), c1: Math.min(n, Math.ceil((x1 - this.win.minX) / c) + 1),
      r0: Math.max(0, Math.floor((z0 - this.win.minZ) / c)), r1: Math.min(n, Math.ceil((z1 - this.win.minZ) / c) + 1),
    };
    if (r.c1 > r.c0 && r.r1 > r.r0) { this.queue.push(r); this.stats.rebuilds++; }
  }

  /** Nearest first: split a rectangle into row strips ordered by distance from the camera. */
  private stripOrder(q: Rect, x: number, z: number): Rect[] {
    const out: Rect[] = [];
    for (let r = q.r0; r < q.r1; r += 8) out.push({ c0: q.c0, c1: q.c1, r0: r, r1: Math.min(q.r1, r + 8) });
    const rowZ = (s: Rect): number => this.win.nodeZ((s.r0 + s.r1) / 2);
    return out.sort((a, b) => Math.abs(rowZ(a) - z) - Math.abs(rowZ(b) - z));
  }

  private countPending(): number {
    let k = 0; const s = this.gpu.src;
    for (let i = 0; i < this.n * this.n; i++) if (s[i * 4 + 2] > 2.5) k++;
    return k;
  }

  /** Build queued cells until `budgetMs` is spent. Returns the cells built. */
  build(g: PuddleGround, budgetMs: number): number {
    if (!this.queue.length) return 0;
    const t0 = performance.now();
    const n = this.n, gpu = this.gpu;
    let built = 0;
    while (this.queue.length && performance.now() - t0 < budgetMs) {
      const q = this.queue[0];
      // one row of the rectangle a pass
      const row = q.r0;
      for (let col = q.c0; col < q.c1; col++) {
        const i = row * n + col;
        const x = this.win.nodeX(col), z = this.win.nodeZ(row);
        const cellS = puddleCell(g, x, z);
        const wasPending = gpu.src[i * 4 + 2] > 2.5;
        gpu.bed[i] = cellS.bed - this.refY;
        packCell(gpu.src, i, cellS);
        if (wasPending) this.pending--;
        built++;
      }
      q.r0++;
      if (q.r0 >= q.r1) this.queue.shift();
    }
    if (built) {
      this.buildEdges(g);
      gpu.markInputsDirty();
      const ms = performance.now() - t0;
      this.stats.cellsBuilt += built; this.stats.buildMs += ms; this.stats.lastBuildMs = ms; this.stats.cellUs = (ms * 1000) / built;
    }
    return built;
  }

  /** The terrain one cell beyond each edge (relative to refY) — water leaves only downhill. */
  private buildEdges(g: PuddleGround): void {
    const n = this.n, c = this.cell, e = this.gpu.edge;
    const at = (x: number, z: number): number => g.height(x, z) - RELIEF_M * reliefDepression(x, z, 0) - this.refY;
    for (let r = 0; r < n; r++) { const z = this.win.nodeZ(r); e[r] = at(this.win.nodeX(n - 1) + c, z); e[n + r] = at(this.win.nodeX(0) - c, z); }
    for (let k = 0; k < n; k++) { const x = this.win.nodeX(k); e[2 * n + k] = at(x, this.win.nodeZ(n - 1) + c); e[3 * n + k] = at(x, this.win.nodeZ(0) - c); }
  }

  /**
   * Set the clock's rates, the render origin, the channel's uniforms; step. Returns the substeps run. The render frame:
   * render x/z = absolute − the anchor (−scene.position), render y = absolute + scene.position.y (`sceneY`).
   */
  step(r: Renderer, dtS: number, rates: PuddleRates, anchorX: number, anchorZ: number, sceneY = 0, maskEvery = MASK_REFRESH_S): number {
    const g = this.gpu;
    (g.uRain as unknown as { value: number }).value = rates.rainMs;
    (g.uEvap as unknown as { value: number }).value = rates.evapMs;
    (g.uInfilScale as unknown as { value: number }).value = rates.infilMs;
    (g.uSheetDrain as unknown as { value: number }).value = rates.rainMs; // the unresolved drains carry off a flat's own rain
    const ox = this.win.minX - anchorX, oz = this.win.minZ - anchorZ;
    (this.uOriginRender.value as THREE.Vector2).set(ox, oz);
    (this.uRefY as unknown as { value: number }).value = this.refY + sceneY;
    (puddleChannel.uMin.value as THREE.Vector2).set(ox, oz);
    (puddleChannel.uCell as unknown as { value: number }).value = this.cell;
    (puddleChannel.uRefY as unknown as { value: number }).value = this.refY + sceneY;
    this.maskClock += dtS;
    if (this.maskClock >= maskEvery) { this.maskClock = 0; g.refreshMask(r); }
    return g.advance(r, dtS);
  }

  /** Read the depth + ledger back; the residual closes the budget over the window since the previous read. */
  async read(r: Renderer): Promise<PuddleReadStats | null> {
    if (this.reading) return this.last;
    this.reading = true;
    try {
      const minX = this.win.minX, minZ = this.win.minZ;
      const p = this.gpu.readback(r);
      this.gpu.resetLedger(r); // queued after the copies: the next read's ledger starts where this read's volume was taken
      const rb = await p;
      const L = rb.ledger;
      const budget = L.rain - L.infiltration - L.evaporation - L.runoff - L.drained - L.boundary - L.shiftedOut + L.clampGain;
      const residual = this.lastVolume === null ? null : rb.volume - this.lastVolume - budget;
      this.lastVolume = rb.volume;
      this.last = { at: performance.now(), volume: rb.volume, wetCells: rb.wetCells, maxDepth: rb.maxDepth, ledger: L, residual, depth: rb.depth, minX, minZ };
      this.stats.readbacks++;
      return this.last;
    } finally { this.reading = false; }
  }

  /** The depth at an absolute point from the last readback (m; null outside / no read). */
  depthAt(x: number, z: number): number | null {
    const s = this.last; if (!s) return null;
    const c = Math.round((x - s.minX) / this.cell), rr = Math.round((z - s.minZ) / this.cell);
    if (c < 0 || rr < 0 || c >= this.n || rr >= this.n) return null;
    return s.depth[rr * this.n + c];
  }

  get retainFilm(): number { return PUDDLE_RETAIN_FILM; }

  dispose(): void { this.gpu.dispose(); }
}

// ── the drawn chunk's rules at a point (the shader's varyings: the same triangle split as meshHeight) ──

interface InterleavedLike { data?: { array: ArrayLike<number>; stride: number }; offset?: number; array?: ArrayLike<number>; itemSize?: number }
/** The attribute's float array + (stride, offset) for vertex i component k. */
function reader(a: InterleavedLike | undefined): ((i: number, k: number) => number) | null {
  if (!a) return null;
  if (a.data && a.data.array) { const arr = a.data.array, st = a.data.stride, off = a.offset ?? 0; return (i, k) => arr[i * st + off + k]; }
  if (a.array) { const arr = a.array, st = a.itemSize ?? 4; return (i, k) => arr[i * st + k]; }
  return null;
}

/**
 * The ground rules at (x, z) from a resident chunk's interleaved attributes (gW0–2, gMask), interpolated over
 * PlaneGeometry's triangles exactly as `meshHeight` interpolates the heights (faces (a, b, d) for u + v ≤ 1, else
 * (b, c, d)) — i.e. what the fragment's varyings hold. null when the chunk carries no v2 attributes.
 */
export function chunkRulesAt(
  chunk: { mesh: THREE.Mesh; seg: number } | null, x: number, z: number, chunkSize: number,
): { weights: number[]; road: number; wet: number; routeD: number } | null {
  if (!chunk) return null;
  const g = chunk.mesh.geometry as unknown as { getAttribute(n: string): InterleavedLike | undefined };
  const W0 = reader(g.getAttribute('gW0')), W1 = reader(g.getAttribute('gW1')), W2 = reader(g.getAttribute('gW2')), M = reader(g.getAttribute('gMask'));
  if (!W0 || !W1 || !W2 || !M) return null;
  const seg = chunk.seg, cellM = chunkSize / seg;
  const cx = Math.floor(x / chunkSize), cz = Math.floor(z / chunkSize);
  const gx = (x - cx * chunkSize) / cellM, gz = (z - cz * chunkSize) / cellM;
  let ix = Math.floor(gx); if (ix >= seg) ix = seg - 1; if (ix < 0) ix = 0;
  let iz = Math.floor(gz); if (iz >= seg) iz = seg - 1; if (iz < 0) iz = 0;
  const u = Math.min(1, Math.max(0, gx - ix)), v = Math.min(1, Math.max(0, gz - iz));
  const side = seg + 1;
  const A = iz * side + ix, B = (iz + 1) * side + ix, C = (iz + 1) * side + ix + 1, D = iz * side + ix + 1;
  const lerp = (f: (i: number, k: number) => number, k: number): number => (u + v <= 1
    ? f(A, k) + (f(D, k) - f(A, k)) * u + (f(B, k) - f(A, k)) * v
    : f(C, k) + (f(B, k) - f(C, k)) * (1 - u) + (f(D, k) - f(C, k)) * (1 - v));
  const weights = [lerp(W0, 0), lerp(W0, 1), lerp(W0, 2), lerp(W0, 3), lerp(W1, 0), lerp(W1, 1), lerp(W1, 2), lerp(W1, 3), lerp(W2, 0), lerp(W2, 1), lerp(W2, 2), lerp(W2, 3)];
  return { weights, road: lerp(M, 0), wet: lerp(M, 2), routeD: lerp(M, 3) };
}
